import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { format } from 'date-fns';

export type TimeFormat = '12h' | '24h';
export type DateFormat = 'eu' | 'us' | 'iso';

interface DateTimeFormatContextValue {
  timeFormat: TimeFormat;
  dateFormat: DateFormat;
  setTimeFormat: (f: TimeFormat) => void;
  setDateFormat: (f: DateFormat) => void;
  /** Format just the time, e.g. "15:43" or "3:43 PM" */
  formatTime: (date: Date | string | number) => string;
  /** Format just the date, e.g. "10.04.2026" or "4/10/2026" or "2026-04-10" */
  formatDate: (date: Date | string | number) => string;
  /** Format date + time combined */
  formatDateTime: (date: Date | string | number) => string;
}

const TIME_STORAGE_KEY = 'time-format';
const DATE_STORAGE_KEY = 'date-format';

const TIME_PATTERNS: Record<TimeFormat, string> = {
  '12h': 'h:mm a',
  '24h': 'HH:mm',
};

const DATE_PATTERNS: Record<DateFormat, string> = {
  eu: 'dd.MM.yyyy',
  us: 'M/d/yyyy',
  iso: 'yyyy-MM-dd',
};

const DateTimeFormatContext = createContext<DateTimeFormatContextValue | null>(null);

function readStoredTime(): TimeFormat {
  const stored = localStorage.getItem(TIME_STORAGE_KEY);
  if (stored === '12h' || stored === '24h') return stored;
  return '24h'; // EU default
}

function readStoredDate(): DateFormat {
  const stored = localStorage.getItem(DATE_STORAGE_KEY);
  if (stored === 'eu' || stored === 'us' || stored === 'iso') return stored;
  return 'eu'; // EU default
}

function toDate(d: Date | string | number): Date {
  return d instanceof Date ? d : new Date(d);
}

export function DateTimeFormatProvider({ children }: { children: ReactNode }) {
  const [timeFormat, setTimeFormatState] = useState<TimeFormat>(readStoredTime);
  const [dateFormat, setDateFormatState] = useState<DateFormat>(readStoredDate);

  useEffect(() => {
    localStorage.setItem(TIME_STORAGE_KEY, timeFormat);
  }, [timeFormat]);

  useEffect(() => {
    localStorage.setItem(DATE_STORAGE_KEY, dateFormat);
  }, [dateFormat]);

  const formatTime = useCallback(
    (d: Date | string | number) => format(toDate(d), TIME_PATTERNS[timeFormat]),
    [timeFormat],
  );

  const formatDate = useCallback(
    (d: Date | string | number) => format(toDate(d), DATE_PATTERNS[dateFormat]),
    [dateFormat],
  );

  const formatDateTime = useCallback(
    (d: Date | string | number) =>
      format(toDate(d), `${DATE_PATTERNS[dateFormat]} ${TIME_PATTERNS[timeFormat]}`),
    [dateFormat, timeFormat],
  );

  return (
    <DateTimeFormatContext.Provider
      value={{
        timeFormat,
        dateFormat,
        setTimeFormat: setTimeFormatState,
        setDateFormat: setDateFormatState,
        formatTime,
        formatDate,
        formatDateTime,
      }}
    >
      {children}
    </DateTimeFormatContext.Provider>
  );
}

export function useDateTimeFormat(): DateTimeFormatContextValue {
  const ctx = useContext(DateTimeFormatContext);
  if (!ctx) throw new Error('useDateTimeFormat must be used within DateTimeFormatProvider');
  return ctx;
}
